import { DIM_EXT } from "building-types/constants/dimensionConstants";
import { dimensionDimlineNew } from "building-types/dimensions/dimensionComponentTools";
import type { DimensionGeometryProps } from "building-types/dimensions/dimensionTypes";
import {
  type GeneratedDimension,
  type DimensionRowOutput,
  type DimensionRowOutputNewProps,
  GeneratedDimensionPlacementType,
} from "building-types/dimensions/dimensionTypes";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import { generatedBuildingEntityHandle } from "building-types/entities/buildingEntityPickHandle";
import type { LevelRoomEdge, RoomEdge } from "building-types/handles/docEntityHandleTypes";
import { EdgeOrientation, EdgeOrientationsArr } from "building-types/handles/docEntityHandleTypes";
import type { HalfEdgeData } from "building-types/provenance/halfEdgeDataTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { ONE_MINUS_DIST_EPSILON } from "geom/core";
import { oppositeHalfEdgeIndex } from "geom/polyclip/dcelFromPolygons";
import type { EdgeRef, PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2EdgeTangent } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

type GeneratedExteriorDimensionNewProps = DimensionRowOutput & Pick<BuildingEntityBase<"DIMENSION">, "docEntityHandle">;

export function generatedExteriorDimensionNew(props: GeneratedExteriorDimensionNewProps): GeneratedDimension {
  const id = uid();

  return {
    entityType: "DIMENSION",
    placement: GeneratedDimensionPlacementType.EXTERIOR,
    id,
    ...props,
    pickHandle: props.docEntityHandle ?? generatedBuildingEntityHandle("DIMENSION", id),
  };
}

export function generatedFoundationExteriorDimensionNew(props: GeneratedExteriorDimensionNewProps): GeneratedDimension {
  const id = uid();

  return {
    entityType: "DIMENSION",
    placement: GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR,
    id,
    ...props,
    pickHandle: props.docEntityHandle ?? generatedBuildingEntityHandle("DIMENSION", id),
  };
}

export function generatedFoundationInteriorDimensionNew(props: GeneratedExteriorDimensionNewProps): GeneratedDimension {
  const id = uid();

  return {
    entityType: "DIMENSION",
    placement: GeneratedDimensionPlacementType.FOUNDATION_INTERIOR,
    id,
    ...props,
    pickHandle: props.docEntityHandle ?? generatedBuildingEntityHandle("DIMENSION", id),
  };
}

export function dimensionRowOutputNew(props: DimensionRowOutputNewProps): DimensionRowOutput {
  return {
    ...props,
    dimLine: dimensionDimlineNew({
      pl: props.pl,
      dimExt: DIM_EXT,
      baseLineOffset: props.baseLineOffset ?? 0,
    }),
  };
}

type GeneratedInteriorDimensionNewProps = DimensionGeometryProps &
  Pick<BuildingEntityBase<"DIMENSION">, "docEntityHandle">;

export function generatedInteriorDimensionNew(props: GeneratedInteriorDimensionNewProps): GeneratedDimension {
  const id = uid();

  return {
    entityType: "DIMENSION",
    placement: GeneratedDimensionPlacementType.INTERIOR,
    id,
    ...props,
    pickHandle: props.docEntityHandle ?? generatedBuildingEntityHandle("DIMENSION", id),
  };
}

export function getMatchingEdgeOrientation(
  dimLine: PolyLine2,
  mirror: boolean,
  reverse = false,
): EdgeOrientation | undefined {
  const [tx, ty] = polyLine2EdgeTangent(dimLine, 0);
  let dirMatch: EdgeOrientation | undefined;
  for (const [direction, dirVec] of EdgeOrientationsArr) {
    if (
      reverse
        ? -tx * dirVec[0] - ty * dirVec[1] > ONE_MINUS_DIST_EPSILON
        : tx * dirVec[0] + ty * dirVec[1] > ONE_MINUS_DIST_EPSILON
    ) {
      dirMatch = direction as EdgeOrientation;
      if (mirror) {
        if (dirMatch === EdgeOrientation.LEFT) {
          dirMatch = EdgeOrientation.RIGHT;
        } else if (dirMatch === EdgeOrientation.RIGHT) {
          dirMatch = EdgeOrientation.LEFT;
        }
      }
    }
  }
  return dirMatch;
}

export function getRoomEdgesFromHalfEdges(
  halfEdges: number[],
  planarGraph: PolygonPlanarGraph,
  roomIdsOnLevel: string[],
  rooms: Table<Room>,
  useOppositeHalfEdge: boolean = true,
): RoomEdge[] {
  const roomEdges: RoomEdge[] = [];

  for (const halfEdgeIndex of halfEdges) {
    const oppHalfEdgeIndex = useOppositeHalfEdge ? oppositeHalfEdgeIndex(halfEdgeIndex) : halfEdgeIndex;
    let result: EdgeRef | undefined;
    try {
      result = planarGraph.halfEdgeIndexToPolygonEdgeRef(oppHalfEdgeIndex);
    } catch {
      continue;
    }
    if (!result || result.edgeIndex === undefined) {
      continue;
    }
    const poly = result.index;
    if (poly === undefined) {
      continue;
    }
    const roomId = roomIdsOnLevel[poly];
    if (!roomId) {
      continue;
    }
    const room = rooms[roomId];
    if (!room) {
      continue;
    }
    const edgeId = room.edgeIds[result.edgeIndex];
    if (edgeId === undefined) {
      continue;
    }
    roomEdges.push({ roomId, edgeId });
  }
  return roomEdges;
}

export function getRoomEdgesFromHalfEdgeData(
  halfEdges: HalfEdgeData[],
  planarGraphs: Table<PolygonPlanarGraph>,
  roomIdsOnLevels: Table<string[]>,
  rooms: Table<Room>,
  useOppositeHalfEdge: boolean = true,
): LevelRoomEdge[] {
  const roomEdges: LevelRoomEdge[] = [];

  for (const halfEdgeData of halfEdges) {
    const planarGraph = planarGraphs[halfEdgeData.levelId];
    if (!planarGraph) {
      continue;
    }
    const roomIdsOnLevel = roomIdsOnLevels[halfEdgeData.levelId];
    if (!roomIdsOnLevel) {
      continue;
    }

    if (halfEdgeData.halfEdge === undefined) {
      return [];
    }
    const oppHalfEdgeIndex = useOppositeHalfEdge ? oppositeHalfEdgeIndex(halfEdgeData.halfEdge) : halfEdgeData.halfEdge;

    let result: EdgeRef | undefined;
    try {
      result = planarGraph.halfEdgeIndexToPolygonEdgeRef(oppHalfEdgeIndex);
    } catch {
      continue;
    }
    if (!result || result.edgeIndex === undefined) {
      continue;
    }
    const poly = result.index;
    if (poly === undefined) {
      continue;
    }
    const roomId = roomIdsOnLevel[poly];
    if (!roomId) {
      continue;
    }
    const room = rooms[roomId];
    if (!room) {
      continue;
    }
    const edgeId = room.edgeIds[result.edgeIndex];
    if (edgeId === undefined) {
      continue;
    }

    roomEdges.push({ levelId: halfEdgeData.levelId, roomId, edgeId, entityLocation: !!halfEdgeData.entityLocation });
  }
  return roomEdges;
}
